import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  heading: "Begin Your Journey",
  subtitle: "Let us craft your perfect Himalayan adventure. Share your preferences and our expert team will design a bespoke journey just for you.",
  whatsappNumber: "+91 8149139073",
};

const ContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', tour: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [contactContent, setContactContent] = useState(DEFAULTS);

  useEffect(() => {
    fetch(`${API_URL}/api/content/site`)
      .then(r => r.json())
      .then(data => { if (data?.contact) setContactContent({ ...DEFAULTS, ...data.contact }); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to submit');
      setIsSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="contact" className="py-32 px-4 bg-zinc-50">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <CheckCircle className="w-20 h-20 text-emerald-600 mx-auto mb-6" />
            <h2 className="text-4xl font-serif mb-4 text-zinc-900">Thank You!</h2>
            <p className="text-xl text-zinc-600 leading-relaxed">
              We've received your message and a confirmation email has been sent to your inbox.
              Our team will get back to you within 24 hours.
            </p>
          </div>
          <Button onClick={() => { setIsSubmitted(false); setFormData({ name: '', email: '', phone: '', tour: '', message: '' }); }}
            className="bg-emerald-800 hover:bg-emerald-900 text-white rounded-sm px-8 py-6">
            Send Another Message
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-32 px-4 bg-zinc-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-serif mb-6 text-zinc-900">{contactContent.heading}</h2>
          <div className="w-24 h-1 bg-amber-600 mx-auto mb-8" />
          <p className="text-xl text-zinc-600 max-w-3xl mx-auto">
            {contactContent.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2">
            <Card className="border-0 shadow-xl rounded-sm">
              <CardContent className="p-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-zinc-700 font-semibold">Full Name *</Label>
                      <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required placeholder="Your full name"
                        className="mt-2 border-2 border-zinc-200 rounded-sm py-6 focus:border-emerald-700" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-zinc-700 font-semibold">Email Address *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required placeholder="your@email.com"
                        className="mt-2 border-2 border-zinc-200 rounded-sm py-6 focus:border-emerald-700" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-zinc-700 font-semibold">Phone Number</Label>
                      <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 XXXXX XXXXX"
                        className="mt-2 border-2 border-zinc-200 rounded-sm py-6 focus:border-emerald-700" />
                    </div>
                    <div>
                      <Label htmlFor="tour" className="text-zinc-700 font-semibold">Interested In</Label>
                      <Input id="tour" value={formData.tour} onChange={(e) => setFormData({ ...formData, tour: e.target.value })}
                        placeholder="e.g., Everest Base Camp Trek"
                        className="mt-2 border-2 border-zinc-200 rounded-sm py-6 focus:border-emerald-700" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-zinc-700 font-semibold">Your Message *</Label>
                    <Textarea id="message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required placeholder="Tell us about your dream adventure..."
                      rows={5} className="mt-2 border-2 border-zinc-200 rounded-sm focus:border-emerald-700" />
                  </div>
                  {error && <p className="text-red-600 text-sm">{error}</p>}
                  <Button type="submit" disabled={isSubmitting}
                    className="w-full bg-emerald-800 hover:bg-emerald-900 text-white py-7 text-lg rounded-sm transition-all duration-300 hover:scale-[1.02]">
                    {isSubmitting ? 'Sending...' : <><Send className="mr-2" /> Send Your Inquiry</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-8">
            <Card className="border-0 shadow-lg rounded-sm overflow-hidden">
              <div className="bg-emerald-800 p-6 text-white">
                <h3 className="text-xl font-semibold mb-2">Get in Touch</h3>
                <p className="text-white/80 text-sm">We'd love to hear from you</p>
              </div>
              <CardContent className="p-6 space-y-4">
                <a href={`tel:${contactContent.whatsappNumber?.replace(/\s/g, '')}`} className="flex items-center gap-3 text-zinc-700 hover:text-emerald-700 transition-colors">
                  <Phone className="w-5 h-5 text-emerald-700" /><span>{contactContent.whatsappNumber}</span>
                </a>
                <a href="mailto:info@trailz.in" className="flex items-center gap-3 text-zinc-700 hover:text-emerald-700 transition-colors">
                  <Mail className="w-5 h-5 text-emerald-700" /><span>info@trailz.in</span>
                </a>
                <div className="flex items-start gap-3 text-zinc-700">
                  <MapPin className="w-5 h-5 text-emerald-700 mt-1" /><span>Baner, Pune, India</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg rounded-sm overflow-hidden bg-gradient-to-br from-green-600 to-green-700 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MessageCircle className="w-8 h-8" />
                  <div>
                    <h3 className="text-lg font-semibold">Chat on WhatsApp</h3>
                    <p className="text-white/80 text-sm">Quick response guaranteed</p>
                  </div>
                </div>
                <a href={`https://wa.me/${contactContent.whatsappNumber?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-white text-green-700 hover:bg-green-50 rounded-sm py-5 font-semibold">
                    Start WhatsApp Chat
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
